import { defineStore } from "pinia";
import router from "../router";
import type { QuizData, Answer, Lead, QuestionAnswer, UrlLogic, UrlVariant } from "../types";

const API_URLS = {
  LEADCHECK: "https://wgtk52k6dqdr5barbgeftvxcye0lqzlw.lambda-url.us-east-1.on.aws/", // new leadclump-quiz-api function urls
  LEAD: "https://fhuezwgl4usr2pxjfxyhjlacgm0coxbh.lambda-url.us-east-1.on.aws/", // new leadclump-quiz-api function urls
  LEADCHECK_BY_HASHED_EMAIL: "https://vu4a4euzjxlsr4cecxfxhv5efq0ilgkk.lambda-url.us-east-1.on.aws/" // new leadclump-quiz-api function urls
};

const FETCH_QUIZ_DATA_URL = (site: string, quizId: string) =>
  `https://cn4s36uzxh.execute-api.us-east-1.amazonaws.com/api/${site}/quiz/${quizId}`;

async function loadQuizData(site: string, quizId: string): Promise<QuizData> {
  let quizDataLocal: QuizData;

  try {
    quizDataLocal = (await import("../quiz.json")).default as QuizData;
  } catch {
    quizDataLocal = {} as QuizData;
  }

  let quizData = quizDataLocal;
  const isDevelopment = import.meta.env.MODE === "development";

  if (isDevelopment && Object.keys(quizData).length > 0) {
    quizData = quizDataLocal;
  } else {
    try {
      console.log("fetching quizData from API...")
      quizData = await fetchQuizDataFromAPI(site, quizId);
    } catch {
      quizData = {} as QuizData;
    }
  }

  if (quizData.facebookpixels && !Array.isArray(quizData.facebookpixels)) {
    quizData.facebookpixels = [quizData.facebookpixels];
  }
  if (quizData.tiktokpixels && !Array.isArray(quizData.tiktokpixels)) {
    quizData.tiktokpixels = [quizData.tiktokpixels];
  }
  if (quizData.googleads && !Array.isArray(quizData.googleads)) {
    quizData.googleads = [quizData.googleads];
  }

  if (import.meta.env.MODE === "development") {
    quizData.url = "http://localhost:5173";
  }
  return quizData;
}

async function fetchQuizDataFromAPI(site: string, quizId: string): Promise<QuizData> {
  try {
    const response = await fetch(FETCH_QUIZ_DATA_URL(site, quizId));
    if (!response.ok) {
      console.error("Error loading quiz.json from API");
      return {} as QuizData;
    }
    return await response.json();
  } catch (error) {
    console.error("Connection error when trying to load quiz.json from API:", error);
    return {} as QuizData;
  }
}

export const useMainStore = defineStore({
  id: "main",
  state: () => ({
    scriptsLoaded: false,
    redirecting: false,
    fetching: false,
    questionAnswers: [] as Answer[],
    currentIndex: 0,
    formLead: { name: "", email: "", phone: "" },
    quizData: {} as QuizData,
    isLoading: true,
    isLeadValidAndSafe: false,
    currentStep: 1,
    totalSteps: 0,
  }),
  getters: {
    currentQuestion(state) {
      return state.quizData.questions?.[state.currentIndex] ?? null;
    },
    questionsLength(state) {
      return state.quizData.questions?.length || 0;
    },
    progressBarPercentage: (state) => {
      if (!state.totalSteps) return 0;
      return Math.round((state.currentStep / state.totalSteps) * 100);
    },
    isLastStep(): boolean {
      return this.currentIndex === this.questionsLength;
    },
    allQuestionsAnswered(state) {
      return (state.questionAnswers.length > 0 && state.questionAnswers.length === (state.quizData.questions?.length || 0));
    },
    quizQuestions(state) {
      return state.quizData.questions;
    },
    quizUrlLogic(state) {
      return state.quizData.url_logic;
    },
    gateData(state) {
      return state.quizData.gate;
    },
    getQuestionById(state) {
      return (question_id: string) => state.quizData.questions?.find((q) => q.id === question_id);
    },
    getLeadAnswers(state) {
      return state.questionAnswers.map((qa) => ({
        id: qa[0],
        answer: qa[1]
      }));
    },
    getLeadAnswerByQuestionId(state) {
      return (question_id: string) => state.questionAnswers.filter((qa) => qa[0] === question_id).map((qa) => qa[1]);
    },
  },
  actions: {
    buildRedirectUrl(
      baseUrl: string | undefined,
      additionalParams?: Record<string, string | undefined>,
      hashParams?: Record<string, string | undefined>
    ): string {
      // Se baseUrl for undefined, usar string vazia como fallback
      const url = baseUrl || '';

      // Filter out undefined values from additionalParams
      const filteredParams: Record<string, string> = Object.entries(additionalParams || {})
        .filter(([, value]) => value !== undefined) // Remove undefined values
        .reduce((acc, [key, value]) => {
          acc[key] = value as string; // Ensure string type
          return acc;
        }, {} as Record<string, string>);

      // Filter out undefined values from hashParams
      const filteredHash: Record<string, string> = Object.entries(hashParams || {})
        .filter(([, value]) => value !== undefined) // Remove undefined values
        .reduce((acc, [key, value]) => {
          acc[key] = value as string; // Ensure string type
          return acc;
        }, {} as Record<string, string>);

      const queryParams = new URLSearchParams(filteredParams);

      let hashParamsString = '';
      if (Object.keys(filteredHash).length > 0) {
        hashParamsString = Object.entries(filteredHash)
          .map(([key, value]) => `${key}=${value}`)
          .join('&');
        hashParamsString =`#${hashParamsString}`;
      }

      // Retornar a URL completa com os parâmetros
      return `${url}?${queryParams.toString()}${hashParamsString}`;
    },
    async initializeQuizData(site: string, quizId: string) {
      this.isLoading = true;
      this.quizData = await loadQuizData(site, quizId);
      this.isLoading = false;
      this.checkRoutes();
      this.totalSteps = this.quizData?.questions?.length || 0;
    },
    pushQuestionAnswer(questionAnswer: Answer) {
      const isDuplicate = this.questionAnswers.some(
        ([qId, answer]) => qId === questionAnswer[0] && answer === questionAnswer[1]
      );

      if (!isDuplicate) {
        this.questionAnswers.push(questionAnswer);

        if (this.currentIndex < this.questionsLength - 1) {
          this.currentIndex += 1;
          this.currentStep = this.currentIndex + 1;
        }
      }
    },
    async apiRequest(url: string, method: string, data: unknown = null) {
      this.fetching = true;
      const options: RequestInit = {
        method,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: data ? JSON.stringify(data) : undefined,
      };

      try {
        const response = await fetch(url, options);
        if (response.ok)
          return await response.json();
        else
          throw response
      } catch (error) {
        console.error(error);
        return error;
      } finally {
        this.fetching = false;
      }
    },
    async checkLead(email: string, site: string): Promise<unknown> {
      this.fetching = true;
      try {
        const response = await fetch(API_URLS.LEADCHECK, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, site }),
        });

        if (!response.ok) {
          throw new Error(`Request error: ${response.status} - ${response.statusText}`);
        }

        const result = await response.json();
        console.log('Verification result:', result);
        return result;
      } catch (error) {
        console.error('Error checking lead:', error);
        throw error;
      } finally {
        this.fetching = false;
      }
    },
    isLeadCheckValid(response: { valid: boolean; safe: boolean }) {
      return response.valid && response.safe;
    },
    async createLead(lead: Lead) {
      const result = await this.apiRequest(API_URLS.LEAD, "POST", lead);
      return result;
    },
    async retrieveLeadcheckByHashedEmail() {
      const hashed_email = this._getCookie('leadclump_e');
      if (hashed_email) {
        const result = await this.apiRequest(API_URLS.LEADCHECK_BY_HASHED_EMAIL, "POST", {"hashed_email": hashed_email});
        return result;
      }
      return null;
    },
    resetQuiz() {
      this.redirecting = true;
      this.questionAnswers = [];
      this.currentIndex = 0;
    },
    redirectToLanding() {
      const quizData = this.quizData;
      const leadAnswers = this.getLeadAnswers;
      const urlLogic = this.quizUrlLogic || [];
      
      let direct_to: string | undefined = undefined;

      // Sort URLs by priority
      urlLogic.sort((a, b) => {
        if (a.priority === undefined && b.priority === undefined) return 0;
        if (a.priority === undefined) return 1;
        if (b.priority === undefined) return -1;
        return a.priority - b.priority;
      });

      // Find the first matching URL logic
      for (const url_logic of urlLogic) {
        const leadAnswerValues = leadAnswers.map((answer) => answer.answer);
        const urlLogicAnswerValues = url_logic.answers.map(answer => answer.value);

        if (!url_logic.answers || url_logic.answers.length === 0 || leadAnswerValues.every((answer) => urlLogicAnswerValues.includes(answer))) {
          direct_to = url_logic.url;

          // Handle url_variants
          if (url_logic.url_variant && url_logic.variant_weight && Number(url_logic.variant_weight) > 0 && Number(url_logic.variant_weight) < 100) {
            if (Math.random() >= (1 - (parseInt(url_logic.variant_weight.toString()) / 100))) {
              direct_to = url_logic.url_variant;
            }
          } else if (url_logic.url_variants) {
            if (Array.isArray(url_logic.url_variants) && url_logic.url_variants.length > 0 && typeof url_logic.url_variants[0] === 'string') {
              const stringVariants = url_logic.url_variants.filter((v): v is string => typeof v === 'string');
              if (stringVariants.length > 0) {
                const index = Math.floor(Math.random() * stringVariants.length);
                direct_to = stringVariants[index];
                break;
              }
            }

            const weightedVariants = url_logic.url_variants.filter((v): v is { weight: number; url: string } => typeof v === 'object' && v !== null && 'weight' in v);
            const totalVariantWeight = weightedVariants.reduce((sum, variant) => sum + variant.weight, 0);
            
            if (totalVariantWeight > 100) {
              direct_to = url_logic.url;
              break;
            }

            if (weightedVariants.length === 0) {
              direct_to = url_logic.url;
              break;
            }

            let cumulativeWeight = 0;
            const variantWeights: number[] = [];
            weightedVariants.forEach(variant => {
              cumulativeWeight += variant.weight / 100;
              variantWeights.push(cumulativeWeight);
            });
            variantWeights.push(1);

            const randomNum = Math.random();
            for (let i = 0; i < variantWeights.length; i++) {
              if (randomNum < variantWeights[i]) {
                direct_to = i < weightedVariants.length ? weightedVariants[i].url : url_logic.url;
                break;
              }
            }
          }

          if (direct_to) {
            break;
          }
        }
      }

      const finalUrl = direct_to || quizData.quiz_default_url;
      
      // Adiciona parâmetros da URL atual
      const queryParams = new URLSearchParams(window.location.search);
      const urlParams = new URLSearchParams();
      
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'sub_id'].forEach(param => {
        const value = queryParams.get(param);
        if (value) urlParams.set(param, value);
      });

      // Set UTM variable
      if (quizData.variable && quizData.value) {
        urlParams.set(quizData.variable, quizData.value)
      } else {
        urlParams.set('utm_content', 'skip');
      }

      const redirect_url = `${finalUrl}?${urlParams.toString()}`;
      
      this.resetQuiz();

      if (window.location !== window.parent.location) {
        window.top!.location.href = redirect_url;
      } else {
        window.location.href = redirect_url;
      }
    },
    setLead() {
      const lead: Lead = {} as Lead;
      const quizData = this.quizData;

      lead.quiz_id = quizData.quiz_id || '';
      lead.site_id = quizData.site_id || '';

      let direct_to: string | undefined = undefined;
      const leadAnswers = this.getLeadAnswers as unknown as QuestionAnswer[];
      const urlLogic: UrlLogic[] = this.quizUrlLogic || [];

      // Sort URLs by priority (lower number = higher priority)
      urlLogic.sort((a, b) => {
        if (a.priority === undefined && b.priority === undefined) return 0;
        if (a.priority === undefined) return 1;
        if (b.priority === undefined) return -1;
        return a.priority - b.priority;
      });

      // Find the first matching URL logic
      for (const url_logic of urlLogic) {
        const leadAnswerValues = leadAnswers.map((answer: QuestionAnswer) => answer.answer);
        const urlLogicAnswerValues = url_logic.answers.map(answer => answer.value);

        if (!url_logic.answers || url_logic.answers.length === 0 || leadAnswerValues.every((answer) => urlLogicAnswerValues.includes(answer))) {
          direct_to = url_logic.url;

          // Handle legacy url_variant with weight
          if (url_logic.url_variant && url_logic.variant_weight && Number(url_logic.variant_weight) > 0 && Number(url_logic.variant_weight) < 100) {
            if (Math.random() >= (1 - (parseInt(url_logic.variant_weight.toString()) / 100))) {
              direct_to = url_logic.url_variant;
            }
          // Handle url_variants array
          } else if (url_logic.url_variants) {
            // Handle legacy string array variants
            if (Array.isArray(url_logic.url_variants) && url_logic.url_variants.length > 0 && typeof url_logic.url_variants[0] === 'string') {
              const stringVariants = url_logic.url_variants as string[];
              if (stringVariants.length > 0) {
                const index = Math.floor(Math.random() * stringVariants.length);
                direct_to = stringVariants[index];
                break;
              }
            }

            // Handle new structure with weights
            const weightedVariants = (url_logic.url_variants as UrlVariant[]).filter((v): v is UrlVariant => typeof v === 'object' && v !== null && 'weight' in v && 'url' in v);
            const totalVariantWeight = weightedVariants.reduce((sum, variant) => sum + variant.weight, 0);
            const totalWeight = 100;

            // Check if weights exceed 100%
            if (totalVariantWeight > totalWeight) {
              console.error('Variant weights exceed 100%. Using default URL.');
              direct_to = url_logic.url;
              break;
            }

            // Handle empty variants array
            if (weightedVariants.length === 0) {
              direct_to = url_logic.url;
              break;
            }

            // Calculate cumulative weights
            let cumulativeWeight = 0;
            const variantWeights: number[] = [];
            weightedVariants.forEach(variant => {
              cumulativeWeight += variant.weight / totalWeight;
              variantWeights.push(cumulativeWeight);
            });

            // Add 100% weight for main URL, if no variant is found, defaults back to main url
            variantWeights.push(1);

            // Select URL based on random number
            const randomNum = Math.random();
            for (let i = 0; i < variantWeights.length; i++) {
              if (randomNum < variantWeights[i]) {
                direct_to = i < weightedVariants.length
                  ? weightedVariants[i].url
                  : url_logic.url;
                break;
              }
            }
          }

          if (direct_to) {
            break;
          }
        }
      }

      lead.direct_to = direct_to || quizData.quiz_default_url;
      lead.referrer = document.referrer;
      lead.origin = window.location.origin + window.location.pathname;

      // Cookies
      lead.fbp = this._getCookie('_fbp');
      lead.gid = this._getCookie('_gid');  // `gid` agora está definido em ExtendedLead

      // Query Parameters
      const queryParams = new URLSearchParams(window.location.search);
      lead.utm_source = queryParams.get('utm_source') || undefined;
      lead.utm_campaign = queryParams.get('utm_campaign') || undefined;
      lead.utm_medium = queryParams.get('utm_medium') || undefined;
      lead.utm_content = queryParams.get('utm_content') || undefined;
      lead.utm_term = queryParams.get('utm_term') || undefined;

      lead.fbclid = queryParams.get('fbclid') || undefined;
      lead.gclid = queryParams.get('gclid') || undefined;
      lead.sub_id = queryParams.get('sub_id') || undefined;

      // Enrich answers with friendly texts
      lead.question_answers = leadAnswers.map(answer => {
        // Search for the corresponding question
        const question = quizData.questions?.find(q => q.id === answer.id);

        // Search for the answer text
        let answerText = answer.answer; // fallback for the original value

        if (question && question.answers) {
          // Check if it's an array (current format of quiz.json)
          const answerData = question.answers.find((ans) => {
            return Array.isArray(ans) && ans[0] === answer.answer;
          }) as string[] | undefined;

          if (answerData && Array.isArray(answerData)) {
            answerText = answerData[1] || answer.answer; // Use raw text without slug conversion
          }
        }
        return {
          id: question ? question.title : answer.id, // Use question title in raw format
          answer: answerText  // Now using friendly text in raw format
        } as QuestionAnswer;
      });

      return lead;
    },
    handleSkip(isAnswer: boolean = false) {
      const quizData = this.quizData;
      const lead = this.setLead() as unknown as Lead;

      const additionalParams = {
        utm_source: lead.utm_source,
        utm_medium: lead.utm_medium,
        utm_campaign: lead.utm_campaign,
        utm_content: lead.utm_content,
        utm_term: lead.utm_term,
        fbclid: lead.fbclid,
        gclid: lead.gclid,
        sub_id: lead.sub_id,
      } as Record<string, string>;

      // Set UTM variable
      if (isAnswer) {
        additionalParams['utm_medium'] = 'skip-answer';
      } else if (quizData.variable && quizData.value) {
        additionalParams[quizData.variable] = quizData.value;
      } else {
        additionalParams['utm_content'] = 'skip';
      }

      const redirect_url = this.buildRedirectUrl(lead.direct_to || '', additionalParams);

      this.resetQuiz();

      if (window.location !== window.parent.location) {
        window.top!.location.href = redirect_url;
      } else {
        window.location.href = redirect_url;
      }
    },
    checkRoutes() {
      if (this.allQuestionsAnswered) {
        this.currentStep = this.totalSteps + 1;
        router.push('/gate');
        return;
      }
      router.push({ name: 'question', query: router.currentRoute.value.query });
    },
    _getCookie(name: string): string {
      try {
        const value = document.cookie
          .split('; ')
          .find(row => row.startsWith(name + '='))
          ?.split('=')[1];
        return value ? decodeURIComponent(value) : '';
      } catch (e) {
        console.error(e);
        return '';
      }
    },
    _setCookie(name: string, value: string, days: number) {
      try {
        let expires = ""
        if (days) {
          const date = new Date()
          date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000))
          expires = "; expires=" + date.toUTCString()
        }
        document.cookie = name + "=" + (("" + value) || "") + expires + "; path=/"
      } catch (e) {
        console.error(e);
      }
    }
  },
});
